import { Button } from '@/components/ui/button.tsx';
import { PDKEmojiReactIcon } from '@/components/ui/pdk-icons.tsx';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx';
import useHoverOrOpenShow from '@/components/ux/hooks/use-hover-or-open-show.ts';
import { useEmojiReactions } from '@/hooks/use-emoji-reactions.ts';
import { cn } from '@/lib/utils.ts';
import type { EmojiReactionType } from '@/models/types.ts';
import { type Emoji, EmojiPicker } from 'frimousse';
import { useState } from 'react';

export type EmojiSelectorProps = {
  targetId: string;
  targetType: EmojiReactionType;
  hoverRef?: React.RefObject<HTMLDivElement | null>;
  buttonClassName?: string;
};

const EmojiSelector = ({
  targetId,
  targetType,
  hoverRef,
  buttonClassName,
}: EmojiSelectorProps) => {
  const [open, setOpen] = useState(false);
  const { toggle } = useEmojiReactions(targetId, targetType);

  const { shouldShow: shouldShowMenu } = useHoverOrOpenShow({
    hoverGroupRef: hoverRef || { current: null },
    open,
  });
  const shouldShow = !hoverRef || shouldShowMenu;

  const handleEmojiSelect = (emoji: Emoji) => {
    toggle(emoji.emoji);
    setOpen(false);
  };

  return (
    <div
      className={cn({
        'opacity-0': !shouldShow,
        'opacity-100': shouldShow,
      })}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className={cn('p-1 h-fit w-fit', buttonClassName)}
                onClick={() => setOpen(!open)}
              >
                <PDKEmojiReactIcon />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>Add reaction</TooltipContent>
        </Tooltip>
        <PopoverContent
          className="border rounded-lg shadow-lg w-fit p-0"
          side="bottom"
          align="center"
        >
          <EmojiPicker.Root
            className="isolate flex h-[368px] w-fit flex-col bg-white rounded-lg"
            columns={9}
            onEmojiSelect={handleEmojiSelect}
          >
            <EmojiPicker.Search className="z-10 mx-2 mt-2 appearance-none rounded-md bg-neutral-100 px-2.5 py-2 text-sm" />
            <EmojiPicker.Viewport className="relative flex-1 outline-hidden">
              <EmojiPicker.Loading className="absolute inset-0 flex items-center justify-center text-neutral-400 text-sm">
                Loading…
              </EmojiPicker.Loading>
              <EmojiPicker.Empty className="absolute inset-0 flex items-center justify-center text-neutral-400 text-sm">
                No emoji found.
              </EmojiPicker.Empty>
              <EmojiPicker.List
                className="select-none pb-1.5"
                components={{
                  CategoryHeader: ({ category, ...props }) => (
                    <div
                      className="bg-white px-3 pt-3 pb-1.5 text-neutral-600 text-xs"
                      {...props}
                    >
                      {category.label}
                    </div>
                  ),
                  Row: ({ children, ...props }) => (
                    <div className="scroll-my-1.5 px-1.5" {...props}>
                      {children}
                    </div>
                  ),
                  Emoji: ({ emoji, ...props }) => (
                    <button
                      className="flex size-8 items-center justify-center rounded-md text-lg data-[active]:bg-neutral-100"
                      {...props}
                    >
                      {emoji.emoji}
                    </button>
                  ),
                }}
              />
            </EmojiPicker.Viewport>
            <div className="border-t border-neutral-200 px-3 py-2 bg-neutral-50 rounded-b-lg">
              <EmojiPicker.ActiveEmoji>
                {({ emoji }) => (
                  <div className="flex items-center gap-2 min-h-4">
                    {emoji ? (
                      <>
                        <span className="text-lg">{emoji.emoji}</span>
                        <span className="text-sm text-neutral-700">
                          {emoji.label}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-neutral-500">
                        Hover over an emoji to see its name
                      </span>
                    )}
                  </div>
                )}
              </EmojiPicker.ActiveEmoji>
            </div>
          </EmojiPicker.Root>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default EmojiSelector;
